import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { components } from '@/api/generated/schema';

export type DashboardOpenVisits = components['schemas']['DashboardOpenVisitsResponse'];
export type DashboardVisit = components['schemas']['DashboardVisitResponse'];

/**
 * ⚠️ Under `['jobs', 'board']`, on purpose (M2-17). The dashboard is a second
 * view of the board's data, so whatever makes the board stale makes this
 * stale. Nesting the key there lets every existing mutation reach it with no
 * new invalidation call to forget:
 * - status and assignment changes on the job page invalidate `['jobs', 'board']`;
 * - visit edits and board drops invalidate `['jobs']`.
 *
 * A key of its own (`['dashboard']`) would have needed a new line in each of
 * those hooks, and every future one. Tested at the app's real staleTime.
 */
export const openVisitsQueryKey = ['jobs', 'board', 'dashboard'] as const;

/**
 * Refetched every minute while the tab is visible, as well as on focus (the
 * app's default). "Late" and "not started" change because TIME passes, with
 * no write anywhere to invalidate anything. A dashboard left open on the
 * office screen has to flip a 9am visit to "not started" at 9am, not at the
 * next click.
 *
 * Not in the background (`refetchIntervalInBackground` stays false). A hidden
 * tab has nobody to tell, and focus refetches the moment someone looks.
 */
export const OPEN_VISITS_REFETCH_MS = 60_000;

/**
 * Thrown when the account's timezone will not resolve, the API's 400 keyed
 * `Timezone`. Told apart because "try again" cannot fix it; the owner has to
 * correct the account's timezone, and the screen says so.
 */
export class DashboardTimezoneError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DashboardTimezoneError';
  }
}

export function useOpenVisits() {
  return useQuery({
    queryKey: openVisitsQueryKey,
    queryFn: async () => {
      const { data, error, response } = await apiClient.GET('/api/v1/dashboard/open-visits');
      if (response.status === 400) {
        const message = (error as { errors?: Record<string, string[]> } | undefined)?.errors
          ?.Timezone?.[0];
        throw new DashboardTimezoneError(
          message ?? 'GET /api/v1/dashboard/open-visits refused: 400',
        );
      }
      if (!response.ok || !data) {
        throw new Error(`GET /api/v1/dashboard/open-visits failed: ${response.status}`);
      }
      return data;
    },
    refetchInterval: OPEN_VISITS_REFETCH_MS,
  });
}
